import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem, Tab, Tabs,
  FormControlLabel, Switch, Radio, RadioGroup, FormLabel
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Dispatch = () => {
  const [tabValue, setTabValue] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [openVehicleDialog, setOpenVehicleDialog] = useState(false);
  const [openDispatchDialog, setOpenDispatchDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [loading, setLoading] = useState(false);

  const [vehicleForm, setVehicleForm] = useState({
    vehicle_number: '', vehicle_type: 'truck', capacity: 0,
    owner: '', insurance_expiry: '',
  });

  const initialDispatchForm = {
    order_type: 'sales_order',           // 'sales_order' | 'purchase_order'
    sales_order: '',
    purchase_order: '',
    is_hired_vehicle: false,
    vehicle: '',
    hired_vehicle_number: '',
    driver: '',
    hired_driver_name: '',
    hired_driver_phone: '',
    driver_phone_override: '',
    pickup_location: '',
    delivery_location: '',
    expected_delivery_date: '',
    loading_quantity: 0,
    status: 'loading',
    notes: '',
  };

  const [dispatchForm, setDispatchForm] = useState(initialDispatchForm);

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchDispatches();
    fetchSalesOrders();
    fetchPurchaseOrders();
  }, []);

  const fetchVehicles = async () => {
    try {
      const r = await api.get('/dispatch/vehicles/');
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setVehicles(data);
    } catch { toast.error('Failed to fetch vehicles'); }
  };

  const fetchDrivers = async () => {
    try {
      const r = await api.get('/dispatch/drivers/');
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setDrivers(data);
    } catch { /* optional */ }
  };

  const fetchDispatches = async () => {
    try {
      const r = await api.get('/dispatch/dispatches/');
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setDispatches(data);
    } catch { toast.error('Failed to fetch dispatches'); }
  };

  const fetchSalesOrders = async () => {
    try {
      const r = await api.get('/sales/orders/');
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setSalesOrders(data.filter(o => o.status !== 'delivered' && o.status !== 'cancelled'));
    } catch (e) { console.error(e); }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const r = await api.get('/procurement/orders/');
      const data = Array.isArray(r.data) ? r.data : (r.data?.results || []);
      setPurchaseOrders(data.filter(o => o.status !== 'received' && o.status !== 'cancelled'));
    } catch (e) { console.error(e); }
  };

  // ---------- Vehicle CRUD (unchanged) ----------

  const handleVehicleSubmit = async () => {
    try {
      if (editingVehicle) {
        await api.put(`/dispatch/vehicles/${editingVehicle.id}/`, vehicleForm);
        toast.success('Vehicle updated');
      } else {
        await api.post('/dispatch/vehicles/', vehicleForm);
        toast.success('Vehicle added');
      }
      setOpenVehicleDialog(false);
      setEditingVehicle(null);
      setVehicleForm({ vehicle_number: '', vehicle_type: 'truck', capacity: 0, owner: '', insurance_expiry: '' });
      fetchVehicles();
    } catch (e) {
      toast.error('Failed to save vehicle');
    }
  };

  const openEditVehicleDialog = (v) => {
    setEditingVehicle(v);
    setVehicleForm({
      vehicle_number: v.vehicle_number || '',
      vehicle_type: v.vehicle_type || 'truck',
      capacity: parseFloat(v.capacity) || 0,
      owner: v.owner || '',
      insurance_expiry: v.insurance_expiry || '',
    });
    setOpenVehicleDialog(true);
  };

  const handleVehicleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await api.delete(`/dispatch/vehicles/${id}/`);
      toast.success('Vehicle deleted');
      fetchVehicles();
    } catch { toast.error('Failed to delete vehicle'); }
  };

  // ---------- Dispatch CRUD ----------

  const handleDispatchSubmit = async () => {
    try {
      // Client-side validation
      if (dispatchForm.order_type === 'sales_order' && !dispatchForm.sales_order) {
        return toast.error('Please select a Sales Order');
      }
      if (dispatchForm.order_type === 'purchase_order' && !dispatchForm.purchase_order) {
        return toast.error('Please select a Purchase Order');
      }
      if (!dispatchForm.pickup_location) {
        return toast.error('Please enter pickup location');
      }
      if (!dispatchForm.delivery_location) {
        return toast.error('Please enter delivery location');
      }
      if (!dispatchForm.is_hired_vehicle && !dispatchForm.vehicle) {
        return toast.error('Please select a company vehicle');
      }
      if (dispatchForm.is_hired_vehicle && !dispatchForm.hired_vehicle_number && !dispatchForm.hired_driver_name) {
        return toast.error('Enter hired vehicle number and/or driver name');
      }

      const submitData = {
        status: dispatchForm.status,
        is_hired_vehicle: dispatchForm.is_hired_vehicle,
        pickup_location: dispatchForm.pickup_location,
        delivery_location: dispatchForm.delivery_location,
        expected_delivery_date: dispatchForm.expected_delivery_date || null,
        loading_quantity: parseFloat(dispatchForm.loading_quantity) || 0,
        notes: dispatchForm.notes || '',
      };

      // Order reference — only one
      if (dispatchForm.order_type === 'sales_order') {
        submitData.sales_order_id = parseInt(dispatchForm.sales_order);
        submitData.purchase_order_id = null;
      } else {
        submitData.purchase_order_id = parseInt(dispatchForm.purchase_order);
        submitData.sales_order_id = null;
      }

      // Vehicle
      if (dispatchForm.is_hired_vehicle) {
        submitData.vehicle = null;
        submitData.hired_vehicle_number = dispatchForm.hired_vehicle_number || null;
        submitData.driver = null;
        submitData.hired_driver_name = dispatchForm.hired_driver_name || null;
        submitData.hired_driver_phone = dispatchForm.hired_driver_phone || null;
      } else {
        submitData.vehicle = parseInt(dispatchForm.vehicle);
        submitData.hired_vehicle_number = null;
        submitData.driver = dispatchForm.driver ? parseInt(dispatchForm.driver) : null;
        submitData.hired_driver_name = null;
        submitData.hired_driver_phone = null;
      }

      submitData.driver_phone_override = dispatchForm.driver_phone_override || null;

      if (editingDispatch) {
        await api.put(`/dispatch/dispatches/${editingDispatch.id}/`, submitData);
        toast.success('Dispatch updated');
      } else {
        await api.post('/dispatch/dispatches/', submitData);
        toast.success('Dispatch created');
      }

      setOpenDispatchDialog(false);
      setEditingDispatch(null);
      setDispatchForm(initialDispatchForm);
      fetchDispatches();
    } catch (error) {
      console.error('Error saving dispatch:', error);
      const data = error.response?.data;
      const msg = data
        ? (typeof data === 'object' ? JSON.stringify(data) : String(data))
        : 'Failed to save dispatch';
      toast.error(msg);
    }
  };

  const openEditDispatchDialog = (d) => {
    setEditingDispatch(d);
    setDispatchForm({
      order_type: d.sales_order ? 'sales_order' : (d.purchase_order ? 'purchase_order' : 'sales_order'),
      sales_order: d.sales_order || '',
      purchase_order: d.purchase_order || '',
      is_hired_vehicle: d.is_hired_vehicle || false,
      vehicle: d.vehicle || '',
      hired_vehicle_number: d.hired_vehicle_number || '',
      driver: d.driver || '',
      hired_driver_name: d.hired_driver_name || '',
      hired_driver_phone: d.hired_driver_phone || '',
      driver_phone_override: d.driver_phone_override || '',
      pickup_location: d.pickup_location || '',
      delivery_location: d.delivery_location || '',
      expected_delivery_date: d.expected_delivery_date
        ? d.expected_delivery_date.slice(0, 10) // yyyy-mm-dd
        : '',
      loading_quantity: parseFloat(d.loading_quantity) || 0,
      status: d.status || 'loading',
      notes: d.notes || '',
    });
    setOpenDispatchDialog(true);
  };

  const handleDispatchDelete = async (id) => {
    if (!window.confirm('Delete this dispatch?')) return;
    try {
      await api.delete(`/dispatch/dispatches/${id}/`);
      toast.success('Dispatch deleted');
      fetchDispatches();
    } catch { toast.error('Failed to delete dispatch'); }
  };

  const getStatusChip = (status) => {
    const colors = {
      loading: 'warning',
      in_transit: 'info',
      delivered: 'success',
      delayed: 'error',
      cancelled: 'error',
    };
    return <Chip label={status.replace('_', ' ').toUpperCase()} color={colors[status] || 'default'} size="small" />;
  };

  // ---------- Render ----------

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Dispatch Management</Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Vehicles" />
        <Tab label="Dispatches" />
      </Tabs>

      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Vehicles</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setEditingVehicle(null);
              setVehicleForm({ vehicle_number: '', vehicle_type: 'truck', capacity: 0, owner: '', insurance_expiry: '' });
              setOpenVehicleDialog(true);
            }}>Add Vehicle</Button>
          </Box>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle Number</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Capacity (kg)</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Insurance Expiry</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vehicles.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center">No vehicles found</TableCell></TableRow>
                  ) : vehicles.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.vehicle_number}</TableCell>
                      <TableCell>{(v.vehicle_type || '').toUpperCase()}</TableCell>
                      <TableCell align="right">{v.capacity}</TableCell>
                      <TableCell>{v.owner}</TableCell>
                      <TableCell>{v.insurance_expiry ? new Date(v.insurance_expiry).toLocaleDateString('en-IN') : 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Chip label={v.is_active ? 'Active' : 'Inactive'} color={v.is_active ? 'success' : 'error'} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditVehicleDialog(v)}><Edit /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleVehicleDelete(v.id)}><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Dispatches</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setEditingDispatch(null);
              setDispatchForm(initialDispatchForm);
              setOpenDispatchDialog(true);
            }}>Create Dispatch</Button>
          </Box>
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dispatch #</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Dispatch Date</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dispatches.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center">No dispatches found</TableCell></TableRow>
                  ) : dispatches.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.dispatch_number}</TableCell>
                      <TableCell>
                        {d.sales_order_number ? `SO: ${d.sales_order_number}` :
                         d.purchase_order_number ? `PO: ${d.purchase_order_number}` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {d.vehicle_number || 'N/A'}
                        {d.is_hired_vehicle && <Chip label="Hired" size="small" color="default" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{d.driver_name || 'N/A'}</TableCell>
                      <TableCell>{new Date(d.dispatch_date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell align="right">{d.loading_quantity}</TableCell>
                      <TableCell align="center">{getStatusChip(d.status)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditDispatchDialog(d)}><Edit /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDispatchDelete(d.id)}><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* Vehicle Dialog (unchanged structure) */}
      <Dialog open={openVehicleDialog} onClose={() => setOpenVehicleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Vehicle Number"
                value={vehicleForm.vehicle_number}
                onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_number: e.target.value })} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Type</InputLabel>
                <Select value={vehicleForm.vehicle_type}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_type: e.target.value })}
                  label="Vehicle Type">
                  <MenuItem value="truck">Truck</MenuItem>
                  <MenuItem value="trailer">Trailer</MenuItem>
                  <MenuItem value="container">Container</MenuItem>
                  <MenuItem value="tanker">Tanker</MenuItem>
                  <MenuItem value="pickup">Pickup</MenuItem>
                  <MenuItem value="van">Van</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Capacity (kg)" type="number"
                value={vehicleForm.capacity}
                onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: parseFloat(e.target.value) })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Owner"
                value={vehicleForm.owner}
                onChange={(e) => setVehicleForm({ ...vehicleForm, owner: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Insurance Expiry" type="date"
                value={vehicleForm.insurance_expiry}
                onChange={(e) => setVehicleForm({ ...vehicleForm, insurance_expiry: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVehicleDialog(false)}>Cancel</Button>
          <Button onClick={handleVehicleSubmit} variant="contained">
            {editingVehicle ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispatch Dialog */}
      <Dialog open={openDispatchDialog} onClose={() => setOpenDispatchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingDispatch ? 'Edit Dispatch' : 'Create Dispatch'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>

            {/* Order reference — radio */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Order Reference</FormLabel>
                <RadioGroup row value={dispatchForm.order_type}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, order_type: e.target.value })}>
                  <FormControlLabel value="sales_order" control={<Radio />} label="Sales Order" />
                  <FormControlLabel value="purchase_order" control={<Radio />} label="Purchase Order" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {dispatchForm.order_type === 'sales_order' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sales Order</InputLabel>
                  <Select value={dispatchForm.sales_order}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, sales_order: e.target.value })}
                    label="Sales Order">
                    {salesOrders.map((o) => (
                      <MenuItem key={o.id} value={o.id}>{o.so_number} — {o.customer?.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {dispatchForm.order_type === 'purchase_order' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Purchase Order</InputLabel>
                  <Select value={dispatchForm.purchase_order}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, purchase_order: e.target.value })}
                    label="Purchase Order">
                    {purchaseOrders.map((o) => (
                      <MenuItem key={o.id} value={o.id}>{o.po_number} — {o.supplier?.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Vehicle source toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={dispatchForm.is_hired_vehicle}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, is_hired_vehicle: e.target.checked })} />}
                label={dispatchForm.is_hired_vehicle ? 'Hired Vehicle' : 'Company Vehicle'}
              />
            </Grid>

            {/* Vehicle + Driver */}
            {dispatchForm.is_hired_vehicle ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Hired Vehicle Number"
                    value={dispatchForm.hired_vehicle_number}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, hired_vehicle_number: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Hired Driver Name"
                    value={dispatchForm.hired_driver_name}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, hired_driver_name: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Hired Driver Phone"
                    value={dispatchForm.hired_driver_phone}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, hired_driver_phone: e.target.value })} />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Vehicle</InputLabel>
                    <Select value={dispatchForm.vehicle}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, vehicle: e.target.value })}
                      label="Company Vehicle">
                      {vehicles.filter(v => v.is_active).map((v) => (
                        <MenuItem key={v.id} value={v.id}>{v.vehicle_number}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Company Driver</InputLabel>
                    <Select value={dispatchForm.driver}
                      onChange={(e) => {
                        const driver = drivers.find(d => d.id === parseInt(e.target.value));
                        setDispatchForm({
                          ...dispatchForm,
                          driver: e.target.value,
                          driver_phone_override: driver?.phone || dispatchForm.driver_phone_override,
                        });
                      }}
                      label="Company Driver">
                      {drivers.filter(d => d.is_active).map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.name} ({d.phone})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Driver Phone (override)"
                    value={dispatchForm.driver_phone_override}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, driver_phone_override: e.target.value })}
                    helperText="Auto-filled from driver; edit if different for this trip"
                  />
                </Grid>
              </>
            )}

            {/* Locations */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Pickup Location" required
                value={dispatchForm.pickup_location}
                onChange={(e) => setDispatchForm({ ...dispatchForm, pickup_location: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Delivery Location" required
                value={dispatchForm.delivery_location}
                onChange={(e) => setDispatchForm({ ...dispatchForm, delivery_location: e.target.value })} />
            </Grid>

            {/* Dates & load */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Expected Delivery Date" type="date"
                value={dispatchForm.expected_delivery_date}
                onChange={(e) => setDispatchForm({ ...dispatchForm, expected_delivery_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Loading Quantity" type="number"
                value={dispatchForm.loading_quantity}
                onChange={(e) => setDispatchForm({ ...dispatchForm, loading_quantity: parseFloat(e.target.value) })} />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={dispatchForm.status}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, status: e.target.value })}
                  label="Status">
                  <MenuItem value="loading">Loading</MenuItem>
                  <MenuItem value="in_transit">In Transit</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="delayed">Delayed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2}
                value={dispatchForm.notes}
                onChange={(e) => setDispatchForm({ ...dispatchForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDispatchDialog(false)}>Cancel</Button>
          <Button onClick={handleDispatchSubmit} variant="contained">
            {editingDispatch ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dispatch;